import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAnalyticsPage } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { OperationsExecutionWidget } from "@/components/pages/analytics/front-desk/components/operations/OperationsExecutionWidget";
import { OperationsMetricChartsSection } from "@/components/pages/analytics/front-desk/components/operations/OperationsMetricChartsSection";
import { computeOperationsChartYMax } from "@/components/pages/analytics/front-desk/components/operations/operationsTabMappers";
import { SERVICES_CONTENT_GAP } from "@/components/pages/analytics/front-desk/components/services/constants";
import { useAdminOperationsHotels } from "../hooks/useAdminOperationsHotels";
import { useAdminTenantTableExport } from "../hooks/useAdminTenantTableExport";
import { AdminOperationsHotelsTable } from "./operations/AdminOperationsHotelsTable";

export const AdminOperationsTab = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { dateRange } = useAnalyticsPage();
  const {
    tenantId,
    summary,
    tableRows,
    executionTrend,
    totalRequestsTrendChart,
    onHoldCanceledChart,
    approvalTimeChart,
    complaintsChart,
    isLoading,
    isError,
    isReady,
  } = useAdminOperationsHotels(dateRange);

  useAdminTenantTableExport({
    tab: "operations",
    dateRange,
    canExport: Boolean(tenantId) && isReady && !isLoading && !isError,
  });

  const totalRequestsTrendYMax = useMemo(
    () =>
      computeOperationsChartYMax(totalRequestsTrendChart.data, [
        "guestServices",
        "roomServices",
        "externalServices",
      ]),
    [totalRequestsTrendChart.data],
  );

  const onHoldCanceledYMax = useMemo(
    () =>
      computeOperationsChartYMax(onHoldCanceledChart.data, [
        "canceledRequests",
        "onHoldRequests",
      ]),
    [onHoldCanceledChart.data],
  );

  const complaintsYMax = useMemo(
    () => computeOperationsChartYMax(complaintsChart.data, ["complaintCount"]),
    [complaintsChart.data],
  );

  const approvalTimeYMax = useMemo(
    () =>
      computeOperationsChartYMax(
        approvalTimeChart.data,
        ["frontdeskApprovalTime"],
        15,
      ),
    [approvalTimeChart.data],
  );

  if (!tenantId) {
    return (
      <p className="text-zinc-500">
        {t("analytics.admin.noTenant", "Tenant is not selected.")}
      </p>
    );
  }

  if (!isReady) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.operations.selectDateRange",
          "Select a date range to view hotel operations.",
        )}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-zinc-500">
        {t(
          "analytics.admin.operations.loadError",
          "Failed to load operations data.",
        )}
      </p>
    );
  }

  return (
    <section
      className={cn(
        "space-y-6 font-sans transition-opacity duration-150",
        isLoading && "opacity-70",
      )}
      style={{ gap: SERVICES_CONTENT_GAP }}
    >
      <OperationsExecutionWidget
        summary={summary}
        executionTrend={executionTrend}
      />

      <AdminOperationsHotelsTable rows={tableRows} loading={isLoading} />

      <OperationsMetricChartsSection
        totalRequestsTrendChart={totalRequestsTrendChart}
        totalRequestsTrendYMax={totalRequestsTrendYMax}
        onHoldCanceledChart={onHoldCanceledChart}
        onHoldCanceledYMax={onHoldCanceledYMax}
        approvalTimeChart={approvalTimeChart}
        approvalTimeYMax={approvalTimeYMax}
        complaintsChart={complaintsChart}
        complaintsYMax={complaintsYMax}
      />
    </section>
  );
};
